import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Shield, Users, FileText, Star, Users2, DollarSign, TrendingUp,
  ShieldCheck, Database, Mail, MessageSquare, ScrollText, Settings,
  Crown, LogOut, ChevronRight, Menu, X
} from 'lucide-react';
import '../../styles/vds-admin.css';

const navItems = [
  { path: '/admin', label: 'Overview', icon: Shield, exact: true },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/records', label: 'Records', icon: FileText },
  { path: '/admin/reviews', label: 'Reviews', icon: Star },
  { path: '/admin/referrals', label: 'Referrals', icon: Users2 },
  { path: '/admin/financial', label: 'Financial', icon: DollarSign },
  { path: '/admin/analytics', label: 'Analytics', icon: TrendingUp },
  { path: '/admin/compliance', label: 'Compliance', icon: ShieldCheck },
  { path: '/admin/gdpr', label: 'GDPR', icon: Database },
  { path: '/admin/contacts', label: 'Contacts', icon: Mail },
  { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { path: '/admin/logs', label: 'Logs', icon: ScrollText },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminLayout = ({ title, breadcrumbs = [], actions, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="vds-admin" data-testid="admin-layout">
      {/* Sticky Header */}
      <header className="vds-admin-header">
        <div className="vds-admin-header-inner">
          {/* Logo */}
          <Link to="/admin" className="vds-admin-logo">
            <div className="vds-admin-logo-icon">
              <Shield className="h-5 w-5" />
            </div>
            <span className="vds-admin-logo-text hidden sm:inline">VETT Admin</span>
          </Link>

          {/* Desktop Navigation - hidden below lg */}
          <nav className="vds-admin-nav hidden lg:flex">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`vds-admin-nav-link ${isActive(item) ? 'active' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Founder link - only for superadmin */}
            {user?.is_superadmin && (
              <Link
                to="/founder/dashboard"
                className={`vds-admin-nav-link ${location.pathname.startsWith('/founder') ? 'active' : ''}`}
              >
                <Crown className="w-4 h-4" />
                Founder
              </Link>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="vds-admin-nav-link"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </nav>

          {/* Mobile: Hamburger + Logout */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={handleLogout}
              className="vds-admin-nav-link"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="vds-admin-nav-link"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E8EDF5] bg-[#FFFFFF]">
            <nav className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left font-mono text-xs ${
                      isActive(item) 
                        ? 'bg-[#1B5AFF]/10 text-[#1B5AFF]' 
                        : 'text-[#5B6472] hover:bg-[#F5F8FF] hover:text-[#0A0F1E]'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {item.label}
                  </button>
                );
              })}
              {user?.is_superadmin && (
                <button
                  onClick={() => handleNavClick('/founder/dashboard')}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left font-mono text-xs ${
                    location.pathname.startsWith('/founder')
                      ? 'bg-[#1B5AFF]/10 text-[#1B5AFF]'
                      : 'text-[#5B6472] hover:bg-[#F5F8FF] hover:text-[#0A0F1E]'
                  }`}
                >
                  <Crown className="w-4 h-4 flex-shrink-0" />
                  Founder
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="vds-admin-main">
        {/* Breadcrumbs */}
        <div className="vds-admin-breadcrumb">
          <Link to="/admin" className="vds-admin-breadcrumb-link">Admin</Link>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight className="vds-admin-breadcrumb-separator" />
              {crumb.to ? (
                <Link to={crumb.to} className="vds-admin-breadcrumb-link">
                  {crumb.label}
                </Link>
              ) : (
                <span className="vds-admin-breadcrumb-current">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Page Header */}
        {(title || actions) && (
          <div className="vds-admin-page-header flex-col sm:flex-row gap-3">
            {title && <h1 className="vds-admin-page-title">{title}</h1>}
            {actions && <div className="vds-admin-page-actions">{actions}</div>}
          </div>
        )}

        {/* Page Content */}
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
